import { render } from "preact";
import { LocationProvider, Router, Route, useLocation } from "preact-iso/router";
import { lazy, Suspense, useEffect, useState } from "preact/compat";

import "./styles/tokens.css";
import "./styles/global.css";
import "./styles/components.css";

import { AppShell } from "./app";
import { DiagnosticsDrawer } from "./components/DiagnosticsDrawer";
import { SettingsModal } from "./components/SettingsModal";
import { TokenGate } from "./components/TokenGate";
import { AddCompanyModal } from "./components/AddCompanyModal";
import { Toast } from "./components/Toast";
import { fetchScanStatus, getToken, setToken } from "./lib/api";
import { useUrlState } from "./hooks/useUrlState";

type AuthState = "checking" | "locked" | "ready";

// Jobs and Pipeline are lazy-loaded route chunks.
const Jobs = lazy(() => import("./routes/jobs"));
const Pipeline = lazy(() => import("./routes/pipeline"));

function RedirectTo({ path }: { path: string }) {
  const location = useLocation();
  useEffect(() => {
    location.route(path);
  }, []);
  return <div class="route-loading">Loading…</div>;
}

function SettingsRedirect({ onOpenSettings }: { onOpenSettings: () => void }) {
  const location = useLocation();
  useEffect(() => {
    onOpenSettings();
    location.route("/jobs");
  }, []);
  return null;
}

function AppRoutes({
  onOpenSettings,
  onOpenAddCompany,
  onOpenDiagnostics,
}: {
  onOpenSettings: () => void;
  onOpenAddCompany: () => void;
  onOpenDiagnostics: () => void;
}) {
  useUrlState();

  return (
    <AppShell
      onOpenSettings={onOpenSettings}
      onOpenAddCompany={onOpenAddCompany}
      onOpenDiagnostics={onOpenDiagnostics}
    >
      <Router>
        <Route path="/" component={() => <RedirectTo path="/jobs" />} />
        <Route
          path="/jobs"
          component={() => (
            <Suspense fallback={<div class="route-loading">Loading…</div>}>
              <Jobs />
            </Suspense>
          )}
        />
        <Route
          path="/pipeline"
          component={() => (
            <Suspense fallback={<div class="route-loading">Loading…</div>}>
              <Pipeline />
            </Suspense>
          )}
        />
        <Route
          path="/settings"
          component={() => <SettingsRedirect onOpenSettings={onOpenSettings} />}
        />
        <Route default component={() => <RedirectTo path="/jobs" />} />
      </Router>
    </AppShell>
  );
}

export function Main() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [authState, setAuthState] = useState<AuthState>("checking");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await fetchScanStatus();
        if (!cancelled) setAuthState("ready");
      } catch (err) {
        if (cancelled) return;
        const status = err && typeof err === "object" && "status" in err ? (err as { status: number }).status : 0;
        if (status === 401) {
          if (getToken()) setToken("");
          setAuthState("locked");
          return;
        }
        setAuthState("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (authState === "checking") {
    return <div class="route-loading">Loading…</div>;
  }

  if (authState === "locked") {
    return <TokenGate onUnlocked={() => setAuthState("ready")} />;
  }

  return (
    <LocationProvider>
      <AppRoutes
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenAddCompany={() => setAddCompanyOpen(true)}
        onOpenDiagnostics={() => setDiagnosticsOpen(true)}
      />

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <AddCompanyModal open={addCompanyOpen} onClose={() => setAddCompanyOpen(false)} />
      <DiagnosticsDrawer open={diagnosticsOpen} onClose={() => setDiagnosticsOpen(false)} />
      <Toast />
    </LocationProvider>
  );
}

render(<Main />, document.getElementById("app")!);
